import { ForeAftEnum, detectBay40sLocation, } from "open-vessel-definition";
import { can40sBeDefinedInBay } from "@tedivo/tedivo-bay-grid-pure";
import { createDictionary } from "@tedivo/tedivo-pure-helpers";
const name = "bay40sLocationValidator";
const okTranslationKey = "validators:bays.bay40sLocation.ok";
const errorTranslationKey = "validators:bays.bay40sLocation.error";
/**
 * Checks that every pair of Bays keeps its 40'+ definitions in the half that
 * `shipData.bay40sLocation` names (or, when the file states none, the half most of
 * the file uses). The same rule the Bay editor applies, see `can40sBeDefinedInBay`,
 * including the trailing-pair exception.
 *
 * Without any convention at all, only a *split* pair (40'+ data in both halves) is
 * reported, because it cannot be drawn: the target is then the half holding more
 * 40'+ data, and AFT when both hold the same.
 *
 * Unpaired Bays with 40s are not this validator's business; see
 * `allBaysWith40sArePairedValidator`.
 */
export const bay40sLocationValidator = {
    name,
    okTranslationKey,
    errorTranslationKey,
    fn: bay40sLocationValidatorFn,
    severity: "error",
    stage: "data",
};
export function bay40sLocationValidatorFn({ data: { json }, }) {
    var _a;
    const baysData = json.baysData || [];
    const detected = detectBay40sLocation(baysData);
    const fromShipData = (_a = json.shipData) === null || _a === void 0 ? void 0 : _a.bay40sLocation;
    const bay40sLocation = fromShipData !== null && fromShipData !== void 0 ? fromShipData : detected.bay40sLocation;
    const bay40sLocationSource = fromShipData !== undefined ? "shipData" : bay40sLocation !== undefined ? "detected" : undefined;
    const baysDict = createDictionary(baysData, (bl) => `${bl.isoBay}-${bl.level}`);
    const misplaced = [];
    detected.pairs.forEach((pair) => {
        const decision = decideTarget(pair, bay40sLocation, bay40sLocationSource, baysDict, baysData);
        if (decision === undefined)
            return;
        const { to, decidedBy } = decision;
        const from = opposite(to);
        misplaced.push({
            level: pair.level,
            fwd: pair.fwd,
            aft: pair.aft,
            from,
            to,
            fromIsoBay: isoBayOfSide(pair, from),
            toIsoBay: isoBayOfSide(pair, to),
            split: pair.split,
            decidedBy,
        });
    });
    const isValid = misplaced.length === 0;
    return {
        name,
        isValid,
        messageTranslationKey: isValid ? okTranslationKey : errorTranslationKey,
        result: { bay40sLocation, bay40sLocationSource, misplaced },
    };
}
function decideTarget(pair, bay40sLocation, source, baysDict, baysData) {
    if (pair.fwdCount === 0 && pair.aftCount === 0)
        return undefined;
    if (bay40sLocation === undefined || source === undefined) {
        // No convention: only a split pair is a problem, and it decides for itself
        if (!pair.split)
            return undefined;
        return pair.location !== undefined
            ? { to: pair.location, decidedBy: "pair" }
            : { to: ForeAftEnum.AFT, decidedBy: "default" };
    }
    const wrongSide = opposite(bay40sLocation);
    const countOnWrongSide = wrongSide === ForeAftEnum.FWD ? pair.fwdCount : pair.aftCount;
    if (countOnWrongSide === 0)
        return undefined;
    const wrongIsoBay = isoBayOfSide(pair, wrongSide);
    const wrongBl = baysDict[`${wrongIsoBay}-${pair.level}`];
    // Same rule as the Bay editor, so the trailing pair keeps its exception
    const allowance = can40sBeDefinedInBay(wrongIsoBay, pair.level, wrongBl === null || wrongBl === void 0 ? void 0 : wrongBl.pairedBay, bay40sLocation, baysData);
    if (allowance.allowed)
        return undefined;
    return { to: bay40sLocation, decidedBy: source };
}
function opposite(side) {
    return side === ForeAftEnum.FWD ? ForeAftEnum.AFT : ForeAftEnum.FWD;
}
function isoBayOfSide(pair, side) {
    return (side === ForeAftEnum.FWD ? pair.fwd : pair.aft);
}
